import React, { useState, useMemo } from 'react'
import { Search, Users, Edit, Trash2, Building2, ClipboardList, RefreshCw, X, AlertTriangle, CalendarClock } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Select,
  SelectOption,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Pagination,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui'
import { LoadingTable } from '../components/ui/loading-states'
import { ErrorState } from '../components/ui/error-state'
import {
  useShiftLimits,
  useDeleteStaffingLimit,
  useGenerateShiftLimits,
  type ShiftLimit,
  type ShiftLimitFilters,
  type GenerateShiftLimitsResponse,
} from '../hooks/useStaffingLimits'
import { usePositions } from '../hooks/usePositions'
import { useDepartmentsForSelect } from '../hooks/useDepartments'
import { useOrganizationsForSelect } from '../hooks/organizations/useOrganizations'
import {
  useStaffingSchedule,
  type StaffingScheduleFilters,
} from '../hooks/useStaffingSchedule'
import { DeleteStaffingLimitDialog } from '../components/staffing-limits/DeleteStaffingLimitDialog'
import { EditShiftLimitDialog } from '../components/staffing-limits/EditShiftLimitDialog'

type TabKey = 'shifts' | 'schedule'

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
]

export function StaffingLimits() {
  const [activeTab, setActiveTab] = useState<TabKey>('shifts')

  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1

  // Лимиты смен: по умолчанию текущий месяц
  const [shiftFilters, setShiftFilters] = useState<ShiftLimitFilters>({
    page: 1,
    limit: 20,
    year: currentYear,
    month: currentMonth,
  })

  // Штатное расписание
  const [scheduleFilters, setScheduleFilters] = useState<StaffingScheduleFilters>({
    page: 1,
    limit: 20,
    sort: 'date:desc',
    isActive: true,
  })

  const [searchTerm, setSearchTerm] = useState('')
  const [positionFilter, setPositionFilter] = useState<string>('')
  const [departmentFilter, setDepartmentFilter] = useState<string>('')
  const [organizationFilter, setOrganizationFilter] = useState<string>('')

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedLimit, setSelectedLimit] = useState<ShiftLimit | null>(null)
  const [limitToDelete, setLimitToDelete] = useState<string | null>(null)
  const [shiftGenResult, setShiftGenResult] = useState<GenerateShiftLimitsResponse | null>(null)
  const [shiftGenError, setShiftGenError] = useState<string | null>(null)

  const {
    data: shiftData,
    isLoading: shiftLoading,
    error: shiftError,
    refetch: refetchShifts,
  } = useShiftLimits(shiftFilters)
  const {
    data: scheduleData,
    isLoading: scheduleLoading,
    error: scheduleError,
    refetch: refetchSchedule,
  } = useStaffingSchedule(scheduleFilters)

  const { data: positionsData } = usePositions({ limit: 100 })
  const { data: organizationsData } = useOrganizationsForSelect()
  // Подразделения фильтруются по выбранной организации
  const { data: departmentsData } = useDepartmentsForSelect(organizationFilter || undefined)
  const deleteLimit = useDeleteStaffingLimit()
  const generateShiftLimits = useGenerateShiftLimits()

  // Пересчёт лимитов смен из штатки 1С за выбранный (или текущий) период
  const handleGenerateFromStaffing = async () => {
    const year = shiftFilters.year || currentYear
    const month = shiftFilters.month || currentMonth
    setShiftGenError(null)
    setShiftGenResult(null)
    try {
      const result = await generateShiftLimits.mutateAsync({ year, month })
      setShiftGenResult(result)
    } catch (error) {
      setShiftGenError(
        (error as { message?: string })?.message || 'Не удалось рассчитать лимиты смен'
      )
    }
  }

  // Filter handlers
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    if (activeTab === 'shifts') {
      setShiftFilters(prev => ({ ...prev, page: 1, search: value || undefined }))
    } else {
      setScheduleFilters(prev => ({ ...prev, page: 1, search: value || undefined }))
    }
  }

  const handlePositionFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setPositionFilter(value)
    if (activeTab === 'shifts') {
      setShiftFilters(prev => ({ ...prev, page: 1, positionId: value || undefined }))
    } else {
      setScheduleFilters(prev => ({ ...prev, page: 1, positionId: value || undefined }))
    }
  }

  const handleDepartmentFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setDepartmentFilter(value)
    if (activeTab === 'shifts') {
      setShiftFilters(prev => ({ ...prev, page: 1, departmentId: value || undefined }))
    } else {
      setScheduleFilters(prev => ({ ...prev, page: 1, departmentId: value || undefined }))
    }
  }

  const handleOrganizationFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setOrganizationFilter(value)
    setDepartmentFilter('')
    if (activeTab === 'shifts') {
      setShiftFilters(prev => ({
        ...prev,
        page: 1,
        organizationId: value || undefined,
        departmentId: undefined,
      }))
    }
  }

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value ? parseInt(e.target.value, 10) : undefined
    setShiftFilters(prev => ({ ...prev, page: 1, year: value }))
  }

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value ? parseInt(e.target.value, 10) : undefined
    setShiftFilters(prev => ({ ...prev, page: 1, month: value }))
  }

  const handleScheduleActiveFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setScheduleFilters(prev => ({
      ...prev,
      page: 1,
      isActive: value === '' ? undefined : value === 'true',
    }))
  }

  // Format helpers
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  const formatYearMonth = (year: number, month: number) => `${MONTH_NAMES[month - 1]} ${year}`

  const formatMoney = (value: number | null) => {
    if (value === null) return '—'
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'KZT', maximumFractionDigits: 0 }).format(value)
  }

  // Action handlers
  const handleEdit = (limit: ShiftLimit) => {
    setSelectedLimit(limit)
    setIsEditModalOpen(true)
  }

  const handleDelete = (id: string) => {
    setLimitToDelete(id)
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (limitToDelete) {
      await deleteLimit.mutateAsync(limitToDelete)
      setIsDeleteModalOpen(false)
      setLimitToDelete(null)
    }
  }

  // Statistics (по текущей странице)
  const shiftStats = useMemo(() => {
    if (!shiftData?.data) return { total: 0, totalShifts: 0, totalUsed: 0, exceeded: 0 }

    return {
      total: shiftData.total,
      totalShifts: shiftData.data.reduce((sum, l) => sum + l.max_shifts, 0),
      totalUsed: shiftData.data.reduce((sum, l) => sum + (l.used_shifts ?? 0), 0),
      exceeded: shiftData.data.filter(l => l.exceeded).length,
    }
  }, [shiftData])

  const scheduleStats = useMemo(() => {
    if (!scheduleData?.data) return { total: 0, totalRates: 0, totalPayroll: 0, activeCount: 0 }

    return {
      total: scheduleData.total,
      totalRates: scheduleData.data.reduce((sum, s) => sum + s.rate_count, 0),
      totalPayroll: scheduleData.data.reduce((sum, s) => sum + (s.payroll_fund || 0), 0),
      activeCount: scheduleData.data.filter(s => s.is_active).length,
    }
  }, [scheduleData])

  const yearOptions = useMemo(() => {
    const years = []
    for (let y = currentYear + 1; y >= currentYear - 5; y--) {
      years.push(y)
    }
    return years
  }, [currentYear])

  // Занятость: цвет по остатку
  const usageBadgeVariant = (limit: ShiftLimit): 'default' | 'secondary' | 'destructive' => {
    if (limit.used_shifts === null) return 'secondary'
    if (limit.exceeded) return 'destructive'
    if (limit.used_shifts >= limit.max_shifts * 0.9) return 'default'
    return 'secondary'
  }

  const error = activeTab === 'shifts' ? shiftError : scheduleError
  const refetch = activeTab === 'shifts' ? refetchShifts : refetchSchedule

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Лимиты смен</h1>
          <p className="text-muted-foreground">
            Лимиты смен из штатного расписания 1С
          </p>
        </div>
        <ErrorState
          title="Ошибка загрузки данных"
          onAction={refetch}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6 flex-1 overflow-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Лимиты смен</h1>
          <p className="text-muted-foreground">
            Месячный лимит смен по должности и подразделению: ставки из штатки 1С × рабочие дни графика
          </p>
        </div>
        {activeTab === 'shifts' && (
          <Button
            variant="outline"
            onClick={handleGenerateFromStaffing}
            disabled={generateShiftLimits.isPending}
            title="Пересчитать лимиты смен из штатного расписания 1С за выбранный период"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${generateShiftLimits.isPending ? 'animate-spin' : ''}`} />
            {generateShiftLimits.isPending ? 'Расчёт...' : 'Пересчитать из 1С'}
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabKey)}>
        <TabsList>
          <TabsTrigger value="shifts">
            <CalendarClock className="mr-2 h-4 w-4" />
            Лимиты смен
          </TabsTrigger>
          <TabsTrigger value="schedule">
            <ClipboardList className="mr-2 h-4 w-4" />
            Штатное расписание
          </TabsTrigger>
        </TabsList>

        {/* Common Filters */}
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="text-lg">Фильтры</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Organization filter */}
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">
                  Организация
                </label>
                <Select
                  value={organizationFilter}
                  onChange={handleOrganizationFilter}
                >
                  <SelectOption value="">Все организации</SelectOption>
                  {organizationsData?.map((org: { id: string; name: string }) => (
                    <SelectOption key={org.id} value={org.id}>
                      {org.name}
                    </SelectOption>
                  ))}
                </Select>
              </div>

              {/* Department filter */}
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">
                  Подразделение
                </label>
                <Select
                  value={departmentFilter}
                  onChange={handleDepartmentFilter}
                  disabled={!organizationFilter}
                >
                  <SelectOption value="">
                    {organizationFilter ? 'Все подразделения' : 'Сначала выберите организацию'}
                  </SelectOption>
                  {departmentsData?.map((dept) => (
                    <SelectOption key={dept.id} value={dept.id}>
                      {dept.name}
                    </SelectOption>
                  ))}
                </Select>
              </div>

              {/* Position filter */}
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">
                  Должность
                </label>
                <Select
                  value={positionFilter}
                  onChange={handlePositionFilter}
                >
                  <SelectOption value="">Все должности</SelectOption>
                  {positionsData?.data.map((pos) => (
                    <SelectOption key={pos.id} value={pos.id}>
                      {pos.name}
                    </SelectOption>
                  ))}
                </Select>
              </div>

              {/* Search */}
              <div className="relative">
                <label className="text-sm font-medium text-muted-foreground mb-1 block">
                  Поиск
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Поиск по должности..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="pl-10"
                  />
                </div>
              </div>
            </div>

            {/* Tab-specific filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              {activeTab === 'shifts' ? (
                <>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">
                      Год
                    </label>
                    <Select
                      value={shiftFilters.year?.toString() || ''}
                      onChange={handleYearChange}
                    >
                      <SelectOption value="">Все годы</SelectOption>
                      {yearOptions.map((year) => (
                        <SelectOption key={year} value={year.toString()}>
                          {year}
                        </SelectOption>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">
                      Месяц
                    </label>
                    <Select
                      value={shiftFilters.month?.toString() || ''}
                      onChange={handleMonthChange}
                    >
                      <SelectOption value="">Все месяцы</SelectOption>
                      {MONTH_NAMES.map((name, idx) => (
                        <SelectOption key={idx + 1} value={(idx + 1).toString()}>
                          {name}
                        </SelectOption>
                      ))}
                    </Select>
                  </div>
                </>
              ) : (
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">
                    Статус
                  </label>
                  <Select
                    value={scheduleFilters.isActive === undefined ? '' : String(scheduleFilters.isActive)}
                    onChange={handleScheduleActiveFilter}
                  >
                    <SelectOption value="">Все</SelectOption>
                    <SelectOption value="true">Активные</SelectOption>
                    <SelectOption value="false">Неактивные</SelectOption>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Shift Limits Tab */}
        <TabsContent value="shifts" className="space-y-4">
          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <CalendarClock className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-2xl font-bold">{shiftStats.total}</p>
                    <p className="text-sm text-muted-foreground">Всего лимитов</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <CalendarClock className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-2xl font-bold">{shiftStats.totalShifts}</p>
                    <p className="text-sm text-muted-foreground">Лимит смен (на стр.)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-2xl font-bold">{shiftStats.totalUsed}</p>
                    <p className="text-sm text-muted-foreground">Занято смен (на стр.)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className={`h-4 w-4 ${shiftStats.exceeded > 0 ? 'text-red-600' : 'text-muted-foreground'}`} />
                  <div>
                    <p className={`text-2xl font-bold ${shiftStats.exceeded > 0 ? 'text-red-600' : ''}`}>
                      {shiftStats.exceeded}
                    </p>
                    <p className="text-sm text-muted-foreground">Перелимитов (на стр.)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Результат пересчёта лимитов смен из 1С */}
          {shiftGenError && (
            <div className="flex items-start justify-between gap-2 text-sm p-3 rounded-md border bg-red-50 border-red-300 text-red-900">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <p>{shiftGenError}</p>
              </div>
              <button onClick={() => setShiftGenError(null)}>
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          {shiftGenResult && (
            <div className="flex items-start justify-between gap-2 text-sm p-3 rounded-md border bg-muted/50">
              <div className="space-y-1">
                <p className="font-medium">
                  Лимиты смен из штатки 1С за {MONTH_NAMES[shiftGenResult.month - 1]} {shiftGenResult.year}:
                  {' '}создано {shiftGenResult.created}, обновлено {shiftGenResult.updated}
                  {shiftGenResult.skipped_manual > 0 && (
                    <>, сохранено ручных {shiftGenResult.skipped_manual}</>
                  )}
                </p>
                {(shiftGenResult.skipped_units.no_position > 0 ||
                  shiftGenResult.skipped_units.no_department > 0 ||
                  shiftGenResult.skipped_units.no_template > 0 ||
                  shiftGenResult.skipped_units.no_organization > 0) && (
                  <p className="flex items-center gap-1.5 text-amber-700">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    Не попали в расчёт штатные единицы: без должности — {shiftGenResult.skipped_units.no_position},
                    {' '}без подразделения — {shiftGenResult.skipped_units.no_department},
                    {' '}без графика — {shiftGenResult.skipped_units.no_template}
                    {shiftGenResult.skipped_units.no_organization > 0 && (
                      <>, без организации — {shiftGenResult.skipped_units.no_organization}</>
                    )}
                  </p>
                )}
              </div>
              <button onClick={() => setShiftGenResult(null)}>
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Shift Limits Table */}
          <Card>
            <CardHeader>
              <CardTitle>Лимиты смен</CardTitle>
              <CardDescription>
                Занято = факт по отметкам за прошедшие дни + график на сегодня и будущее.
                Именно этот лимит блокирует создание исключений в графике
              </CardDescription>
            </CardHeader>
            <CardContent>
              {shiftLoading ? (
                <LoadingTable rows={shiftFilters.limit || 20} />
              ) : shiftData && shiftData.data.length > 0 ? (
                <div className="space-y-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Период</TableHead>
                        <TableHead>Организация</TableHead>
                        <TableHead>Подразделение</TableHead>
                        <TableHead>Должность</TableHead>
                        <TableHead className="text-center">Сотрудников</TableHead>
                        <TableHead className="text-center">Лимит</TableHead>
                        <TableHead className="text-center">Занято</TableHead>
                        <TableHead className="text-center">Осталось</TableHead>
                        <TableHead className="text-center">Источник</TableHead>
                        <TableHead className="text-right">Действия</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {shiftData.data.map((limit) => (
                        <TableRow key={limit.id}>
                          <TableCell>
                            <span className="font-medium">
                              {formatYearMonth(limit.year, limit.month)}
                            </span>
                          </TableCell>
                          <TableCell>{limit.organization?.name || 'Не указана'}</TableCell>
                          <TableCell>
                            {limit.department ? (
                              <span>{limit.department.name}</span>
                            ) : (
                              <span className="text-muted-foreground">Вся организация</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">
                              {limit.position?.name || 'Не указана'}
                            </span>
                          </TableCell>
                          <TableCell className="text-center text-muted-foreground">
                            {limit.employees_count ?? '—'}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="font-bold">
                              {limit.max_shifts}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {limit.used_shifts === null ? (
                              <span className="text-muted-foreground">—</span>
                            ) : (
                              <Badge variant={usageBadgeVariant(limit)} className="font-bold">
                                {limit.used_shifts}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {limit.used_shifts === null ? (
                              <span className="text-muted-foreground">—</span>
                            ) : limit.exceeded ? (
                              <span className="font-medium text-red-600">
                                −{limit.used_shifts - limit.max_shifts}
                              </span>
                            ) : (
                              <span className="font-medium">{limit.available}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge
                              variant="outline"
                              title={
                                limit.source === '1c_staffing'
                                  ? 'Рассчитано из штатного расписания 1С'
                                  : 'Изменено вручную — автосинк не перезаписывает'
                              }
                            >
                              {limit.source === '1c_staffing' ? '1С' : 'Вручную'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center gap-2 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(limit)}
                                title="Изменить лимит"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(limit.id)}
                                title="Удалить"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {shiftData.totalPages > 1 && (
                    <div className="flex justify-center">
                      <Pagination
                        currentPage={shiftData.page}
                        totalPages={shiftData.totalPages}
                        onPageChange={(page) => setShiftFilters(prev => ({ ...prev, page }))}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CalendarClock className="mx-auto h-12 w-12 text-muted-foreground/50" />
                  <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                    Лимиты смен не найдены
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Лимиты создаются автоматически после синхронизации штатного расписания из 1С.
                    Можно пересчитать вручную за выбранный период.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={handleGenerateFromStaffing}
                    disabled={generateShiftLimits.isPending}
                  >
                    <RefreshCw className={`mr-2 h-4 w-4 ${generateShiftLimits.isPending ? 'animate-spin' : ''}`} />
                    Пересчитать из 1С
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Staffing Schedule Tab */}
        <TabsContent value="schedule" className="space-y-4">
          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <ClipboardList className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-2xl font-bold">{scheduleStats.total}</p>
                    <p className="text-sm text-muted-foreground">Всего позиций</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-2xl font-bold">{scheduleStats.activeCount}</p>
                    <p className="text-sm text-muted-foreground">Активных (на стр.)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-2xl font-bold">{scheduleStats.totalRates}</p>
                    <p className="text-sm text-muted-foreground">Ставок (на стр.)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-2xl font-bold">{formatMoney(scheduleStats.totalPayroll)}</p>
                    <p className="text-sm text-muted-foreground">ФОТ (на стр.)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Schedule Table */}
          <Card>
            <CardHeader>
              <CardTitle>Штатное расписание</CardTitle>
              <CardDescription>
                Данные из 1С — штатные единицы, ставки, графики, оклады
              </CardDescription>
            </CardHeader>
            <CardContent>
              {scheduleLoading ? (
                <LoadingTable rows={scheduleFilters.limit || 20} />
              ) : scheduleData && scheduleData.data.length > 0 ? (
                <div className="space-y-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Код</TableHead>
                        <TableHead>Наименование</TableHead>
                        <TableHead>Подразделение</TableHead>
                        <TableHead>Должность</TableHead>
                        <TableHead className="text-center">Ставки</TableHead>
                        <TableHead>График</TableHead>
                        <TableHead className="text-right">Оклад</TableHead>
                        <TableHead className="text-right">ФОТ</TableHead>
                        <TableHead className="text-center">Статус</TableHead>
                        <TableHead>Дата</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scheduleData.data.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <span className="font-mono text-sm">{item.code}</span>
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">{item.name}</span>
                          </TableCell>
                          <TableCell>
                            {item.department?.name || item.department_name}
                          </TableCell>
                          <TableCell>
                            {item.position?.name || item.position_name}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary" className="font-bold">
                              {item.rate_count}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {item.work_schedule || '—'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            {formatMoney(item.salary)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatMoney(item.payroll_fund)}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant={item.is_active ? 'default' : 'outline'}>
                              {item.is_active ? 'Активна' : 'Неактивна'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {formatDate(item.date)}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {scheduleData.totalPages > 1 && (
                    <div className="flex justify-center">
                      <Pagination
                        currentPage={scheduleData.page}
                        totalPages={scheduleData.totalPages}
                        onPageChange={(page) => setScheduleFilters(prev => ({ ...prev, page }))}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ClipboardList className="mx-auto h-12 w-12 text-muted-foreground/50" />
                  <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                    Штатное расписание не найдено
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Данные загружаются из 1С через синхронизацию.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <EditShiftLimitDialog
        open={isEditModalOpen}
        onOpenChange={(open) => {
          setIsEditModalOpen(open)
          if (!open) setSelectedLimit(null)
        }}
        limit={selectedLimit}
      />

      <DeleteStaffingLimitDialog
        open={isDeleteModalOpen}
        onOpenChange={(open) => {
          setIsDeleteModalOpen(open)
          if (!open) setLimitToDelete(null)
        }}
        onConfirm={handleConfirmDelete}
        isLoading={deleteLimit.isPending}
      />
    </div>
  )
}
